<template>
	<div class="d-employee-en-page">
		<EmployeePageHeader :intro="intro" :details="details" english-layout />

		<ScopedColorTheme id="d-employee-en-page__container" theme="blue">
			<LongReadController v-slot="longReadController">
				<!-- Control bar -->
				<div
					v-if="hasListContent"
					ref="controlBar"
					:class="[
						'd-employee-en-page__control-bar',
						'sticky w-full py-2xs/v px-layout-margin bg-white',
						'flex flex-col >=768:flex-row justify-items-stretch items-center gap-xs/v >=768:gap-1/12col',
					]"
				>
					<div
						class="
							order-2
							>=768:order-1
							px-4
							w-full
							>=768:w-auto >=768:flex-1
							overflow-hidden
						"
					>
						<Transition
							name="t-employee-en-page__toc"
							mode="out-in"
							duration="500"
							@leave="onContentToggle"
						>
							<SearchListLayoutToc
								:key="`state-${isFiltered}`"
								class="w-full >=1024:w-fit max-w-full"
								v-bind="longReadController"
								align="left"
							></SearchListLayoutToc>
						</Transition>
					</div>

					<button
						:class="[
							'h-40 px-20 flex items-center justify-center gap-4xs/h rounded-full',
							'border border-theme-20 text-theme text-body-xs',
							'hover:bg-theme-10 w-full >=768:w-auto',
							'order-1 >=768:order-2 flex-shrink-0 flex-grow-0',
						]"
						@click="() => (isFiltered = !isFiltered)"
					>
						<span
							v-text="
								isFiltered
									? 'View all content'
									: 'View peer-reviewed publications only'
							"
						></span>

						<SvgNotPeerReviewed v-if="isFiltered" class="h-16" />
						<SvgPeerReviewed v-else class="h-16" />
					</button>
				</div>

				<Transition
					name="t-employee-en-page__content"
					mode="out-in"
					duration="500"
					@leave="onContentToggle"
				>
					<div
						ref="container"
						:key="`state-${isFiltered}`"
						class="
							w-12/12col
							mx-auto
							flex flex-col
							gap-3xl/v
							pt-2xl/v
						"
					>
						<!-- Content (Teaser, blocks, etc.) -->
						<div
							v-if="
								!isFiltered &&
								((blocks && blocks.length) ||
									(intro && intro.teaser))
							"
							:class="[
								'd-employee-en-page__content',
								'relative',

								{
									'd-employee-en-page__content--collapsed':
										isCollapsed,
								},
							]"
						>
							<div
								:class="[
									'w-full overflow-hidden',
									'duration-500 ease-silk-out',
								]"
								:style="{ maxHeight: `${contentMaxHeight}px` }"
							>
								<div
									ref="content"
									:class="[
										'block relative >=1024:w-8/12col ml-auto',
										'flex flex-col gap-lg/v pb-sm/v',
									]"
								>
									<div
										v-if="intro && intro.teaser"
										ref="teaser"
										class="
											text-red text-sub-teaser
											font-light
										"
										v-text="intro.teaser"
									></div>

									<BlockList
										v-if="blocks && blocks.length"
										:block-list="blocks"
									/>
								</div>
							</div>

							<Transition name="t-show-more">
								<button
									v-if="isCollapsed"
									:class="[
										'flex items-center justify-center',
										'absolute bottom-0 left-1/2',
										'w-40 h-40 rounded-full border border-theme text-theme',
										'bg-gray-light hover:bg-theme-10',
									]"
									style="
										z-index: 2;
										transform: translate(-50%, 50%);
									"
									@click="openContent"
								>
									<SvgWedge class="w-10 mt-1" />
								</button>
							</Transition>
						</div>

						<!-- Selected publications -->
						<div
							v-if="
								!isFiltered &&
								highlightedPublicationsComputed.items &&
								highlightedPublicationsComputed.items.length
							"
						>
							<BaseH3
								class="font-light mb-sm/v"
								v-text="'Selected publications'"
							></BaseH3>

							<div class="flex flex-col gap-3xs/v">
								<ListItem
									v-for="item in highlightedPublicationsComputed.items"
									:key="item.key || item.id"
									:heading-level-offset="2"
									v-bind="item"
									filled
								/>
							</div>
						</div>

						<!-- Selected research project -->
						<div
							v-if="
								!isFiltered &&
								highlightedProjects.items &&
								highlightedProjects.items.length
							"
						>
							<BaseH3
								class="font-light mb-sm/v"
								v-text="'Selected research projects'"
							></BaseH3>

							<div class="flex flex-col gap-3xs/v">
								<ListItem
									v-for="item in highlightedProjects.items"
									:key="item.key || item.id"
									:heading-level-offset="2"
									v-bind="item"
									filled
								/>
							</div>
						</div>

						<!-- Full publication list -->
						<div
							v-if="publicationsComputed.length"
							id="d-employee-en-page__publications"
						>
							<!--
								Hidden table of contents element,
								used to make sure that a publication subtitle
								is not selected in the longread, when at the
								top of the page.
							-->
							<LongReadTarget
								v-if="
									publications.title &&
									publications.title.length
								"
								v-show="!isFiltered"
								:link-to-toc="{
									name: `Spring til toppen`,
									attrs: { 'data-focus-ignore': true },
									setHash: false,
								}"
								:meta-data="{ hidden: true }"
								:forced-order="[-1]"
								container-id="d-employee-en-page__publications"
								title="Publications"
							>
								<BaseH2
									class="font-bold mb-sm/v"
									v-text="publications.title"
								></BaseH2>
							</LongReadTarget>

							<div class="flex flex-col gap-3xl/v">
								<div
									v-for="(
										group, index
									) in publicationsComputed"
									:id="`group-${group.id}`"
									:key="group.id || group.name"
								>
									<LongReadTarget
										v-if="group.name && group.name.length"
										:link-to-toc="{
											name: `Spring til &quot;${group.name}&quot; i indholdsfortegnelsen`,
											attrs: {
												'data-focus-ignore': true,
											},
											setHash: false,
										}"
										:meta-data="{
											total: group.items.length,
										}"
										:container-id="`group-${group.id}`"
										:title="group.name"
										:forced-order="[index]"
									>
										<BaseH3
											class="font-light mb-sm/v"
											v-text="group.name"
										></BaseH3>
									</LongReadTarget>

									<div class="flex flex-col gap-3xs/v">
										<ListItem
											v-for="item in group.items"
											:key="item.key || item.id"
											:heading-level-offset="2"
											v-bind="item"
										/>
									</div>
								</div>
							</div>
						</div>

						<!-- Knowledge areas -->
						<div>
							<KnowledgeAreaBox
								v-if="details.areas && details.areas.length > 0"
								:items="details.areas"
								:title="details.areasTitle"
								class="w-full"
							/>
						</div>
					</div>
				</Transition>
			</LongReadController>
		</ScopedColorTheme>
	</div>
</template>

<script>
import SvgWedge from '~/assets/svgs/icon-wedge.svg?inline';
import SvgPeerReviewed from '~/assets/svgs/icon-peer-reviewed.svg?inline';
import SvgNotPeerReviewed from '~/assets/svgs/icon-not-peer-reviewed.svg?inline';
import BlockList from '~/components/main/BlockList';
import ScopedColorTheme from '~/components/main/ScopedColorTheme.vue';
import LongReadController from '~/components/shared/LongRead/LongReadController.vue';
import LongReadTarget from '~/components/shared/LongRead/LongReadTarget.vue';
import SearchListLayoutToc from '~/components/main/SearchListLayoutToc.vue';
import EmployeePageHeader from '~/components/main/EmployeePageHeader.vue';
import ListItem from '~/components/main/ListItem.vue';
import KnowledgeAreaBox from '~/components/main/KnowledgeAreaBox.vue';

export default {
	name: 'MainEmployeeEnPage',

	components: {
		SvgWedge,
		SvgPeerReviewed,
		SvgNotPeerReviewed,

		BlockList,
		ScopedColorTheme,
		LongReadController,
		LongReadTarget,
		SearchListLayoutToc,

		EmployeePageHeader,
		ListItem,
		KnowledgeAreaBox,
	},

	props: {
		id: [Number, String],
		intro: { type: Object, default: () => ({}) },
		details: { type: Object, default: () => ({}) },

		blocks: { type: Array, default: () => [] },
		publications: { type: Object, default: () => ({}) },
		highlightedPublications: { type: Object, default: () => ({}) },
		highlightedProjects: { type: Object, default: () => ({}) },
	},

	data() {
		return {
			groups: [],
			contentMaxHeight: 480,

			isCollapsed: true,
			isFiltered: false,
			isLoading: false,
		};
	},

	computed: {
		highlightedPublicationsComputed() {
			if (this.isFiltered) {
				return {
					...this.highlightedPublications,
					items: this.highlightedPublications.items.filter(
						(item) => item.peerReview
					),
				};
			}

			return this.highlightedPublications;
		},

		publicationsComputed() {
			if (this.isFiltered) {
				return this.groups
					.map((group) => ({
						...group,
						items: group.items.filter((item) => item.peerReview),
					}))
					.filter((group) => group.items.length);
			}

			return this.groups;
		},

		hasListContent() {
			return [
				this.highlightedPublications?.items ?? [],
				this.highlightedProjects?.items ?? [],
				...this.groups.map((group) => group.items),
			]
				.filter(Boolean)
				.some((items) => items.length);
		},
	},

	mounted() {
		if (this.publications) {
			const endpointUrl = new URL(
				this.publications.endpointUrl,
				'https://www.example.com/'
			);

			(this.publications.fields || []).forEach((field) => {
				endpointUrl.searchParams.set(field.name, field.value);
			});

			this.$axios
				.get(
					[endpointUrl.pathname, endpointUrl.search]
						.filter(Boolean)
						.join('')
				)
				.then((res) => {
					this.groups = res.data.groups;
					this.isLoading = false;
				})
				.catch(() => {
					this.isLoading = false;
				});
		}
	},

	methods: {
		openContent() {
			const content = this.$refs.content?.getBoundingClientRect();

			this.isCollapsed = false;
			this.contentMaxHeight = content?.height;
		},

		onContentToggle(target) {
			const height =
				this.$refs.controlBar?.getBoundingClientRect().height;

			let offset =
				document
					.querySelector('.c-site-header')
					?.getBoundingClientRect().height ?? 0;

			if (target.offsetTop > window.scrollY) {
				offset = 0;
			}

			offset += height ?? 0;

			this.isCollapsed = true;
			this.contentMaxHeight = 480;

			try {
				window.scrollTo({
					top: target.offsetTop - offset,
					behavior: 'smooth',
				});
			} catch (e) {
				window.scrollTo(0, target.offsetTop - offset);
			}
		},
	},
};
</script>

<style lang="postcss">
.d-employee-en-page__control-bar {
	box-shadow: 0px 4px 24px 2px rgba(86, 86, 86, 0.12);
	top: var(--site-header-height, 0px);
	min-height: 96px;
	z-index: 4 !important;

	& * {
		scroll-margin-top: var(--site-header-height, 0px);
	}
}

.d-employee-en-page__control-bar:before {
	@apply absolute bg-white;
	@apply w-screen h-full top-0 left-1/2 transform -translate-x-1/2;

	content: '';
	z-index: -1;
}

.d-employee-en-page__content::after {
	@apply absolute bottom-0 left-0 w-full;
	@apply duration-500 ease-in-out;

	content: '';
	height: 260px;

	transform: translate3d(0, 100%, 0);
	opacity: 0;
	pointer-events: none;

	background: linear-gradient(
		0deg,
		#f1f1f1 29.28%,
		rgba(241, 241, 241, 0.49) 65.81%,
		rgba(241, 241, 241, 0) 91.63%
	);
}

.d-employee-en-page__content--collapsed::after {
	transform: translate3d(0, 0, 0);
	opacity: 1;
}

.t-show-more-leave-active {
	@apply duration-500 ease-silk-out;
}
.t-show-more-leave-to {
	@apply opacity-0;
	transform: translateY(64px);
}

.d-employee-en-page:before {
	@apply bg-gray-light;
	content: '';
	display: block;
	position: fixed;
	left: -50vw; /* for some reason blur triggers weird stuff if just 0 0 and 100vw and vh */
	top: 0;
	width: 200vw;
	height: calc(
		100vh + 9999999px
	); /* and for some reason the blur cuts the bottom of the page if just 100vh / treats it as semi-absolute? */
	z-index: -1;
}

.t-employee-en-page__content-enter-active,
.t-employee-en-page__content-leave-active,
.t-employee-en-page__toc-enter-active,
.t-employee-en-page__toc-leave-active {
	@apply duration-500 ease-silk-out;
}

.t-employee-en-page__content-enter,
.t-employee-en-page__content-leave-to,
.t-employee-en-page__toc-enter,
.t-employee-en-page__toc-leave-to {
	@apply opacity-0;
}
</style>
